import { useCallback, useEffect, useRef, useState } from 'react';
import { AppHeader } from '../components/AppHeader';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Icon } from '../components/Icon';
import { LoadingView } from '../components/LoadingView';
import { Notice } from '../components/Notice';
import type { MovieFrame, Project } from '../domain/model';
import { safeMovieTitle } from '../domain/model';
import { useObjectUrl } from '../hooks/useObjectUrl';
import { captureFrame, closeCamera, openCamera } from '../services/camera';
import { addFrame, deleteFrame, getFrame, getProject } from '../storage/projectRepository';

interface CaptureScreenProps {
  projectId: string;
  onHome: () => void;
  onPreview: () => void;
}

type CameraState = 'closed' | 'opening' | 'open' | 'error';

export function CaptureScreen({ projectId, onHome, onPreview }: CaptureScreenProps) {
  const [project, setProject] = useState<Project>();
  const [lastFrame, setLastFrame] = useState<MovieFrame>();
  const [cameraState, setCameraState] = useState<CameraState>('closed');
  const [cameraMessage, setCameraMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [onionSkin, setOnionSkin] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | undefined>(undefined);
  const captureLock = useRef(false);
  const lastUrl = useObjectUrl(lastFrame?.blob);

  const loadLastFrame = useCallback(async (movie: Project) => {
    const frameId = movie.frameIds.at(-1);
    setLastFrame(frameId ? await getFrame(frameId) : undefined);
  }, []);

  useEffect(() => {
    let active = true;
    void getProject(projectId).then(async (movie) => {
      if (!active || !movie) return;
      setProject(movie);
      await loadLastFrame(movie);
    }).catch(() => setSaveMessage('This movie could not be opened. Your other movies are still safe.'));
    return () => { active = false; };
  }, [loadLastFrame, projectId]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        closeCamera(streamRef.current);
        streamRef.current = undefined;
        setCameraState('closed');
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      closeCamera(streamRef.current);
    };
  }, []);

  async function startCamera() {
    if (!videoRef.current || cameraState === 'opening') return;
    setCameraState('opening');
    setCameraMessage('');
    try {
      streamRef.current = await openCamera(videoRef.current);
      setCameraState('open');
    } catch (error) {
      setCameraState('error');
      setCameraMessage(error instanceof Error ? error.message : 'The camera did not open. Please try again.');
    }
  }

  async function takePicture() {
    if (captureLock.current || !videoRef.current || cameraState !== 'open') return;
    captureLock.current = true;
    setSaving(true);
    setSaveMessage('Saving picture…');
    try {
      const image = await captureFrame(videoRef.current);
      const saved = await addFrame(projectId, image);
      setProject(saved.project);
      setLastFrame(saved.frame);
      setSaveMessage(`Picture ${saved.project.frameIds.length} saved!`);
      navigator.vibrate?.(35);
    } catch (error) {
      setSaveMessage(error instanceof Error ? `${error.message} Your earlier pictures are still safe.` : 'That picture was not saved. Please try again.');
    } finally {
      setSaving(false);
      captureLock.current = false;
    }
  }

  async function removeLastPicture() {
    if (!project || !lastFrame) return;
    try {
      const result = await deleteFrame(project.id, lastFrame.id);
      setProject(result.project);
      await loadLastFrame(result.project);
      setSaveMessage('Last picture removed.');
    } catch {
      setSaveMessage('That picture could not be removed. It is still safe.');
    } finally {
      setDeleteOpen(false);
    }
  }

  if (!project && !saveMessage) return <LoadingView />;
  if (!project) {
    return <main className="centered"><Notice tone="error" role="alert">{saveMessage}</Notice><button className="button primary" onClick={onHome}>Go home</button></main>;
  }

  return (
    <div className="screen-shell capture-shell">
      <AppHeader title={safeMovieTitle(project.title, project.createdAt)} onHome={onHome} />
      <main className="capture-screen">
        <div className="capture-topline">
          <div className="frame-count" aria-live="polite"><strong>{project.frameIds.length}</strong><span>{project.frameIds.length === 1 ? 'picture' : 'pictures'}</span></div>
          <label className="toggle-label">
            <input type="checkbox" checked={onionSkin} onChange={(event) => setOnionSkin(event.target.checked)} disabled={!lastFrame} />
            <span className="toggle" aria-hidden="true" />
            Onion Skin
          </label>
        </div>
        <section className="camera-stage" aria-label="Camera view">
          <video ref={videoRef} playsInline muted aria-label="Live camera picture" />
          {onionSkin && lastUrl && <img className="onion-layer" src={lastUrl} alt="Previous picture guide" />}
          {cameraState !== 'open' && (
            <div className="camera-cover">
              <Icon name="camera" />
              <h2>{cameraState === 'opening' ? 'Opening camera…' : 'Ready for the camera?'}</h2>
              <p>We only use it to take your movie pictures. Nothing is uploaded.</p>
              {cameraMessage && <Notice tone="error" role="alert">{cameraMessage}</Notice>}
              <button className="button primary" type="button" onClick={() => { void startCamera(); }} disabled={cameraState === 'opening'}>
                {cameraState === 'opening' ? 'Please wait…' : cameraState === 'error' ? 'Try Again' : 'Open Camera'}
              </button>
            </div>
          )}
        </section>
        <div className="capture-status" role="status" aria-live="polite">{saveMessage || 'Move something a tiny bit, then take a picture.'}</div>
        <div className="capture-controls">
          <button className="last-frame-button" type="button" disabled={!lastFrame || saving} onClick={() => setDeleteOpen(true)} aria-label="Delete or retake the last picture">
            {lastUrl ? <img src={lastUrl} alt="Last picture" /> : <span aria-hidden="true">—</span>}
            <small><Icon name="trash" /> Retake</small>
          </button>
          <button className="shutter" type="button" disabled={cameraState !== 'open' || saving} onClick={() => { void takePicture(); }} aria-label={saving ? 'Saving picture' : 'Take a picture'}>
            <span aria-hidden="true" />
          </button>
          <button className="play-capture" type="button" disabled={!project.frameIds.length || saving} onClick={onPreview}>
            <Icon name="play" /><small>Play</small>
          </button>
        </div>
        <button className="button done-button" type="button" onClick={onPreview} disabled={!project.frameIds.length || saving}><Icon name="done" /> Done</button>
      </main>
      <ConfirmDialog
        open={deleteOpen}
        title="Retake the last picture?"
        message="We’ll remove the last picture so you can take it again."
        confirmLabel="Yes, retake it"
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => { void removeLastPicture(); }}
      />
    </div>
  );
}
